import { Button } from '@neo/index'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Tooltip } from '@neo/primitives/tooltip'
import { useUpdateNodeInternals } from '@xyflow/react'
import { Eye, SquareMousePointer } from 'lucide-react'
import { useState } from 'react'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { useGetFlowRunDetailsQuery } from '../../queries'
import { NodeName } from '../components/node-name'
import { useFlowRunStore } from '../store'
import type { NodeExecutedEvent } from '../types'
import { buildCanvasIdResolver, highlightSingleNodeAndEdge } from '../utils'
import { ConversationQuality } from './conversation-quality'
import { ConversationQualityPanel } from './conversation-quality-panel'

interface HeaderProps {
  node: NodeExecutedEvent
}

export const NodeExecutionHeaderV2 = ({ node }: HeaderProps) => {
  const { fitView, getNode } = useTypedReactFlow()
  const updateInternals = useUpdateNodeInternals()
  const { data: flowRunDetails } = useGetFlowRunDetailsQuery(node.flow_run_uuid)
  const { aoConversationQualityEnabled } = useFeatureFlags()
  const { executionHistory } = useFlowRunStore()
  const [open, setOpen] = useState(false)
  const nodeId = node.current_node_uuid
  const chosenPath = node.chosen_path
  const previousNode = node.previous_node_uuid
  const currentNodeIndex = executionHistory.findIndex(
    (event) => event.node_run_uuid === node.node_run_uuid,
  )
  const nextNode = currentNodeIndex !== -1 ? executionHistory[currentNodeIndex + 1] : undefined

  const run = flowRunDetails?.data?.run
  const nodeEvaluation = run?.node_metrics?.node_evaluations?.find(
    (evaluation) => evaluation.node_run_uuid === node.node_run_uuid,
  )

  const handleGoToNode = () => {
    if (!nodeId) return

    const toCanvasId = chosenPath
      ? highlightSingleNodeAndEdge(nodeId, chosenPath, previousNode)
      : buildCanvasIdResolver()
    const canvasNodeId = toCanvasId(nodeId)
    if (!getNode(canvasNodeId)) return

    fitView({
      nodes: [{ id: canvasNodeId }],
      duration: 300,
      maxZoom: 0.9,
    })

    if (chosenPath) updateInternals(canvasNodeId)
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-2">
      <div className="flex min-w-0 items-center gap-1">
        <NodeName
          name={node.current_node}
          type={node.current_node_type}
          className="text-h4-500 text-primary"
        />
        {nodeId && (
          <Button
            variant="ghost"
            size="iconXs"
            tooltipContent="Go to node"
            aria-label="Go to node"
            onClick={handleGoToNode}
            data-testid="button-go-to-node"
          >
            <SquareMousePointer size={12} />
          </Button>
        )}
      </div>
      {nodeEvaluation &&
        (aoConversationQualityEnabled ? (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="font-inter text-xs-400 text-secondary cursor-pointer flex min-w-0 items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                data-testid="button-view-conversation-quality"
              >
                <span>View Conversation Quality</span>
                <Eye size={16} className="text-foreground hover:text-primary transition-colors" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="end"
              collisionPadding={16}
              className="w-[540px] max-w-[calc(100vw-2rem)] p-0"
            >
              <ConversationQualityPanel
                nodeEvaluation={nodeEvaluation}
                chosenPath={nextNode?.chosen_path}
                onClose={() => setOpen(false)}
              />
            </PopoverContent>
          </Popover>
        ) : (
          <Tooltip
            delayDuration={0}
            side="bottom"
            align="end"
            collisionPadding={16}
            className="w-96 max-w-[calc(100vw-2rem)] max-h-[min(70vh,var(--radix-tooltip-content-available-height,70vh))] overflow-y-auto subtle-scrollbar border border-border bg-popover p-4 text-popover-foreground"
            // @ts-expect-error tooltip content is not typed
            content={<ConversationQuality nodeEvaluation={nodeEvaluation} nextNode={nextNode} />}
          >
            <button
              type="button"
              className="font-inter text-xs-400 text-secondary flex min-w-0 items-center gap-1 rounded hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="button-view-conversation-quality"
            >
              <span>View Conversation Quality</span>
              <Eye size={16} aria-hidden />
            </button>
          </Tooltip>
        ))}
    </div>
  )
}
